import { action, computed, makeObservable, observable, observableRef, override } from 'mobx';
import { type GradeDetails, MunsellMetric } from '@cutwise/color-visualizer';
import { isArray } from '@cutwise/utils';

import { dictionaryRepository } from '@/common/repositories/DictionaryRepository';
import Storage, { KEY_FCRF_DEMO } from '@/common/services/Storage';
import { BaseDictItem, FancyGradeDictItem } from '@/common/types/dictionaryItem';
import { FCRFGrade, FCRFGradeModel } from '@/fcrf/entities/FCRFGrade';
import { DMC_REL, HTML_REPORT_REL, I3D_REPORT_REL } from '@/media/entity/Media';
import { I_ASET_BLACK, I_ASET_WHITE } from '@/media/entity/SetupPreset';
import {
  CODE_BRILLIANCE,
  CODE_COLOR,
  CODE_DEAD_ZONES,
  CODE_FIRE,
  CODE_FLUOR,
  CODE_INTEGRAL,
  CODE_INTEGRAL_NORM,
  CODE_INTEGRAL_V2,
  CODE_PAVILLION_COLOR_VIBOX,
  CODE_SCINTILLATION,
  CODE_SPREAD,
  CODE_SYMMETRY,
} from '@/metric/constants/codes';
import BrightnessMetric from '@/metric/entity/BrightnessMetric';
import ColorMetric from '@/metric/entity/ColorMetric';
import DeadZoneMetric from '@/metric/entity/DeadZoneMetric';
import FireMetric from '@/metric/entity/FireMetric';
import FluorMetric from '@/metric/entity/FluorMetric';
import IntegralMetric from '@/metric/entity/IntegralMetric';
import IntegralV2Metric from '@/metric/entity/IntegralV2Metric';
import PavillionColorViboxMetric from '@/metric/entity/PavillionColorViboxMetric';
import ScintillationMetric from '@/metric/entity/ScintillationMetric';
import SpreadMetric from '@/metric/entity/SpreadMetric';
import SymmetryMetric from '@/metric/entity/SymmetryMetric';
import Basis from '@/plotting/entity/Basis';
import Plotting from '@/plotting/entity/Plotting';
import { FancyColorSubgradeType } from '@/product/constants/FancyColorSubgradeType';
import Certification from '@/product/entity/Certification';
import { GlobalAccessTarget } from '@/security/entities/GlobalAccessTarget';

import {
  TYPE_DIAMOND_COLORLESS,
  TYPE_DIAMOND_FANCY,
  TYPE_LGD_COLORLESS,
  TYPE_LGD_FANCY,
} from '../constants/productTypes';

import { SHAPE_ROUND } from './CutShape';
import { FancyColorSubgradesModel } from './FancyColorSubgrades';
import Product, { ProductModel, ProductScoresModel } from './Product';
import ReportData from './ReportData';
import ReportImage from './ReportImage';
import Rough from './Rough';

export interface DiamondDetailsModel {
  blackInc: string[];
  openInc: string[];
  treatment: string[];
  whiteInc: string[];
}

export enum DiamondKind {
  Scan = 'scan',
  Plan = 'plan',
  Design = 'design',
}

export type ChangeableDiamondKind = Exclude<DiamondKind, DiamondKind.Scan>;

export class DiamondModel extends Product {
  isFancyColor!: boolean;
  rough!: Nullable<Rough> | Nullable<number>;
  fancyColorDescModifier!: Nullable<BaseDictItem> | Nullable<number>;
  fancyColorDescHue!: Nullable<BaseDictItem> | Nullable<number>;
  fancyColorDistribution!: Nullable<string>;
  color!: Nullable<BaseDictItem> | Nullable<number>;
  clarity!: Nullable<BaseDictItem> | Nullable<number>;
  milky!: Nullable<unknown>;
  clarityVisibility!: Nullable<unknown>;
  fancyGrade!: Nullable<BaseDictItem> | Nullable<number>;
  cutQuality!: Nullable<BaseDictItem> | Nullable<number>;
  symmetry!: Nullable<BaseDictItem> | Nullable<number>;
  fluorescenceStrength!: Nullable<BaseDictItem> | Nullable<number>;
  fluorescenceColor!: Nullable<BaseDictItem> | Nullable<number>;
  girdleThicknessGrade!: Nullable<BaseDictItem> | Nullable<number>;
  girdleThicknessMax!: Nullable<BaseDictItem> | Nullable<number>;
  girdleFinish!: Nullable<unknown>;
  culetSizeGrade!: Nullable<BaseDictItem> | Nullable<number>;
  polish!: Nullable<BaseDictItem> | Nullable<number>;
  details!: DiamondDetailsModel;
  colorGrade!: string;
  certifications!: Certification[];
  kind!: DiamondKind;
  fcrfGrade!: Nullable<FCRFGradeModel>;
  fancyColorSubgrades!: Nullable<FancyColorSubgradesModel>;
}

export default class Diamond extends DiamondModel {
  isFancyColor: boolean = false;

  rough: Nullable<Rough> = null;

  fancyColorDescModifier: Nullable<BaseDictItem> | Nullable<number> = null;

  fancyColorDescHue: Nullable<BaseDictItem> | Nullable<number> = null;

  fancyColorDistribution: Nullable<string> = null;

  color: Nullable<BaseDictItem> = null;

  clarity: Nullable<BaseDictItem> = null;

  blackInc: string[] = [];

  whiteInc: string[] = [];

  openInc: string[] = [];

  treatment: string[] = [];

  milky: Nullable<number> = null;

  clarityVisibility: Nullable<unknown> = null;

  fancyGrade: Nullable<FancyGradeDictItem> = null;

  cutQuality: Nullable<BaseDictItem> = null;

  symmetry: Nullable<BaseDictItem> = null;

  fluorescenceStrength: Nullable<BaseDictItem> = null;

  fluorescenceColor: Nullable<BaseDictItem> = null;

  girdleThicknessGrade: Nullable<BaseDictItem> = null;

  girdleThicknessMax: Nullable<BaseDictItem> = null;

  girdleFinish: Nullable<unknown> = null;

  culetSizeGrade: Nullable<BaseDictItem> = null;

  polish: Nullable<BaseDictItem> = null;

  details: DiamondDetailsModel = {
    blackInc: [],
    openInc: [],
    treatment: [],
    whiteInc: [],
  };

  plottings: Nullable<Plotting[]> = null;

  bases: Nullable<Basis[]> = null;

  colorGrade: string = '';

  certifications: Certification[] = [];

  fireMetric: Nullable<FireMetric> = null;

  scintillationMetric: Nullable<ScintillationMetric> = null;

  brightnessMetric: Nullable<BrightnessMetric> = null;

  fluorMetric: Nullable<FluorMetric> = null;

  symmetryMetric: Nullable<SymmetryMetric> = null;

  integralMetric: Nullable<IntegralMetric> = null;

  integralV2Metric: Nullable<IntegralV2Metric> = null;

  integralNormMetric: Nullable<IntegralMetric> = null;

  spreadMetric: Nullable<SpreadMetric> = null;

  colorMetric: Nullable<ColorMetric> = null;

  deadZoneMetric: Nullable<DeadZoneMetric> = null;

  pavillionColorViboxMetric: Nullable<PavillionColorViboxMetric> = null;

  fancyColorGrade: Nullable<MunsellMetric> = null;

  kind: DiamondKind = DiamondKind.Scan;

  reportData: Nullable<ReportData> = null;

  i3dReport: string = '';

  i3dcompReport: string = '';

  htmlReport: string = '';

  dmc: string = '';

  reportImages: Nullable<ReportImage[]> = null;

  nextAmount: string = '';

  fcrfGrade: Nullable<FCRFGrade> = null;

  constructor(modelData: DiamondModel) {
    super(modelData);

    makeObservable<Diamond, 'applyDemoFCRFGrade'>(this, {
      isFancyColor: observable,
      rough: observable,
      fancyColorDescModifier: observable,
      fancyColorDescHue: observable,
      fancyColorDistribution: observable,
      color: observable,
      clarity: observable,
      blackInc: observable,
      whiteInc: observable,
      openInc: observable,
      treatment: observable,
      milky: observable,
      clarityVisibility: observable,
      fancyGrade: observable,
      cutQuality: observable,
      symmetry: observable,
      fluorescenceStrength: observable,
      fluorescenceColor: observable,
      girdleThicknessGrade: observable,
      girdleThicknessMax: observable,
      girdleFinish: observable,
      culetSizeGrade: observable,
      polish: observable,
      details: observable,
      plottings: observable,
      bases: observable,
      certifications: observable,
      fireMetric: observableRef,
      scintillationMetric: observableRef,
      brightnessMetric: observableRef,
      fluorMetric: observableRef,
      symmetryMetric: observableRef,
      integralMetric: observableRef,
      integralV2Metric: observableRef,
      integralNormMetric: observableRef,
      spreadMetric: observableRef,
      colorMetric: observableRef,
      deadZoneMetric: observableRef,
      pavillionColorViboxMetric: observableRef,
      fancyColorGrade: observableRef,
      kind: observable,
      reportData: observable,
      i3dReport: observable,
      i3dcompReport: observable,
      htmlReport: observable,
      dmc: observable,
      reportImages: observableRef,
      nextAmount: observable,
      fcrfGrade: observable,
      type: override,
      cutShapeId: computed,
      hasPolishMedia: computed,
      hasReports: computed,
      i3dReportLink: computed,
      i3dComparativeLink: computed,
      dmcModelLink: computed,
      htmlReportLink: computed,
      hasPlottings: computed,
      correctBases: computed,
      hasCorrectBases: computed,
      isFancyCut: computed,
      isPatentedCut: computed,
      certificate: computed,
      isValid: override,
      isScan: computed,
      hasSpreadData: computed,
      hasFCRFGrade: computed,
      gradeDetails: computed,
      setBases: action,
      applyDemoFCRFGrade: action,
      populate: override,
    });

    this.populate(modelData);
  }

  get type() {
    if (this.isFancyColor === true && this.isLabGrown === false) {
      return TYPE_DIAMOND_FANCY;
    }

    if (this.isFancyColor === true && this.isLabGrown === true) {
      return TYPE_LGD_FANCY;
    }

    if (this.isFancyColor === false && this.isLabGrown === false) {
      return TYPE_DIAMOND_COLORLESS;
    }

    if (this.isFancyColor === false && this.isLabGrown === true) {
      return TYPE_LGD_COLORLESS;
    }

    return TYPE_DIAMOND_COLORLESS;
  }

  get cutShapeId() {
    if (!this.cutShape) {
      return null;
    }

    return this.cutShape.id;
  }

  get hasPolishMedia() {
    return Boolean(
      this.mediaCollection && this.mediaCollection.polishImages && this.mediaCollection.polishImages.length > 0,
    );
  }

  get hasReports() {
    return Boolean(
      this.i3dReportLink || this.i3dComparativeLink || this.dmcModelLink || this.htmlReportLink || this.ox2zScene,
    );
  }

  get i3dReportLink() {
    let i3dReportLink = this.i3dReport;

    if (i3dReportLink) {
      return i3dReportLink;
    }

    const i3dReports = this.mediaCollection.i3dReports;

    if (i3dReports && i3dReports.length !== 0) {
      i3dReportLink = i3dReports[0].getLink(I3D_REPORT_REL);
    }

    return i3dReportLink;
  }

  get i3dComparativeLink() {
    const i3dCompReportLink = this.i3dcompReport;

    if (i3dCompReportLink) {
      return i3dCompReportLink;
    }

    let i3dComparativeLink = null;
    const i3dComparativeReports = this.mediaCollection.i3dComparativeReports;

    if (i3dComparativeReports && i3dComparativeReports.length !== 0) {
      i3dComparativeLink = i3dComparativeReports[0].getLink(I3D_REPORT_REL);
    }

    return i3dComparativeLink;
  }

  get dmcModelLink() {
    let dmcModelLink = this.dmc;

    if (dmcModelLink) {
      return dmcModelLink;
    }

    const dmcModels = this.mediaCollection.dmcModels;

    if (dmcModels && dmcModels.length !== 0) {
      dmcModelLink = dmcModels[0].getLink(DMC_REL);
    }

    return dmcModelLink;
  }

  get htmlReportLink() {
    let htmlReportLink = this.htmlReport;

    if (htmlReportLink) {
      return htmlReportLink;
    }

    const htmlReports = this.mediaCollection.htmlReports;

    if (htmlReports && htmlReports.length !== 0) {
      htmlReportLink = htmlReports[0].getLink(HTML_REPORT_REL);
    }

    return htmlReportLink;
  }

  get hasPlottings() {
    return this.plottings && this.plottings.length > 0;
  }

  get correctBases() {
    const hasBases = this.bases && this.bases.length > 0;

    if (!hasBases || !this.hasMedia) {
      return [];
    }

    const spOrder = [I_ASET_WHITE, I_ASET_BLACK];
    const orderedBases = this.orderBasesBySetupPresetIds(spOrder);

    if (!orderedBases || orderedBases.length === 0) {
      return [];
    }

    const firstBasis = orderedBases[0];

    return [firstBasis];
  }

  get hasCorrectBases() {
    return this.correctBases && this.correctBases.length > 0;
  }

  get isFancyCut() {
    return this.cutShapeId !== SHAPE_ROUND;
  }

  get isPatentedCut() {
    return this.cutShape && this.cutShape.cutShapePatents && this.cutShape.cutShapePatents.length > 0;
  }

  get certificate() {
    return this.certifications && this.certifications.length > 0 ? this.certifications[0] : null;
  }

  get isValid() {
    return this.carat >= 0.01 && !!this.cutShape;
  }

  get isScan() {
    return this.kind === DiamondKind.Scan;
  }

  get hasSpreadData() {
    if (!this.mediaCollection) {
      return false;
    }

    return Boolean(this.mediaCollection.hasSpreadMedia && this.spreadMetric);
  }

  get hasFCRFGrade() {
    return Boolean(this.fcrfGrade?.innerGrade && this.fcrfGrade?.dispersion && this.fcrfGrade?.undertone);
  }

  get gradeDetails(): GradeDetails {
    return {
      colorName: this.fancyColorSubgrades?.hue?.isccNbs ?? null,
      uniformity: dictionaryRepository.getSubgradeTitle(
        FancyColorSubgradeType.Uniformity,
        this.fancyColorSubgrades?.uniformity,
      ),
      innerGrade: this.fcrfGrade?.innerGrade,
      dispersion: this.fcrfGrade?.dispersion,
      undertone: this.fcrfGrade?.undertone,
    };
  }

  setBases(bases: Basis[]) {
    this.bases = bases;
  }

  getColorTitle(short = false) {
    if (this.color) {
      return short ? this.color.short : this.color.title;
    }

    return this.colorGrade;
  }

  private orderBasesBySetupPresetIds(ids: number[]) {
    const mediaSources = this.mediaCollection.mediaItems.map((i) => i.mediaSources).flat();

    if (!isArray(this.bases) || !mediaSources || mediaSources.length === 0) {
      return [];
    }

    return ids
      .map((spId) => {
        return this.bases!.find((b) => {
          if (!b.compositeImg || !b.overlayImg) {
            return null;
          }

          const mediaSource = mediaSources.find((ms) => ms.id === b.imgMediaSourceId);

          if (!mediaSource) {
            return null;
          }

          const basisSpId = mediaSource.media.setupPreset.id;

          if (!basisSpId || basisSpId !== spId) {
            return null;
          }

          return b;
        });
      })
      .filter((b): b is Basis => Boolean(b));
  }

  getMetricByKey<T extends keyof ProductScoresModel>(key: T): Nullable<ProductScoresModel[T]> {
    if (this._embedded && this._embedded.scores && this._embedded.scores[key]) {
      return this._embedded.scores[key];
    }

    return null;
  }

  populate(modelData: DiamondModel) {
    if (modelData.rough && (modelData.rough as Rough).id) {
      modelData.rough = (modelData.rough as Rough).id;
    }

    super.populate(modelData);

    if (modelData.rough && Number.isInteger(modelData.rough)) {
      this.rough = new Rough({ id: modelData.rough } as unknown as ProductModel);
    }

    this.fcrfGrade = modelData.fcrfGrade ? new FCRFGrade(modelData.fcrfGrade) : null;

    this.applyDemoFCRFGrade();

    if (this._embedded && this._embedded.reportData && Object.keys(this._embedded.reportData).length !== 0) {
      this.reportData = new ReportData(this._embedded.reportData);
    }

    if (this._embedded && this._embedded.reportImg && this._embedded.reportImg.length !== 0) {
      this.reportImages = this._embedded.reportImg.map((ri) => new ReportImage(ri));
    }

    if (this._embedded && this._embedded.i3dReport) {
      this.i3dReport = this._embedded.i3dReport;
    }

    if (this._embedded && this._embedded.i3dcompReport) {
      this.i3dcompReport = this._embedded.i3dcompReport;
    }

    if (this._embedded && this._embedded.htmlReport) {
      this.htmlReport = this._embedded.htmlReport;
    }

    if (this._embedded && this._embedded.dmc) {
      this.dmc = this._embedded.dmc;
    }

    if (this.getMetricByKey(CODE_FIRE)) {
      this.fireMetric = new FireMetric(this.getMetricByKey(CODE_FIRE)!);
    }

    if (this.getMetricByKey(CODE_SCINTILLATION)) {
      this.scintillationMetric = new ScintillationMetric(this.getMetricByKey(CODE_SCINTILLATION)!);
    }

    if (this.getMetricByKey(CODE_BRILLIANCE)) {
      this.brightnessMetric = new BrightnessMetric(this.getMetricByKey(CODE_BRILLIANCE)!);
    }

    if (this.getMetricByKey(CODE_FLUOR)) {
      this.fluorMetric = new FluorMetric(this.getMetricByKey(CODE_FLUOR)!);
    }

    if (this.getMetricByKey(CODE_SYMMETRY)) {
      this.symmetryMetric = new SymmetryMetric(this.getMetricByKey(CODE_SYMMETRY)!);
    }

    if (this.getMetricByKey(CODE_INTEGRAL)) {
      this.integralMetric = new IntegralMetric(this.getMetricByKey(CODE_INTEGRAL)!);
    }

    if (this.getMetricByKey(CODE_INTEGRAL_V2)) {
      this.integralV2Metric = new IntegralV2Metric(this.getMetricByKey(CODE_INTEGRAL_V2)!);
    }

    if (this.getMetricByKey(CODE_INTEGRAL_NORM)) {
      this.integralNormMetric = new IntegralMetric(this.getMetricByKey(CODE_INTEGRAL_NORM)!);
    }

    if (this.getMetricByKey(CODE_SPREAD)) {
      this.spreadMetric = new SpreadMetric(this.getMetricByKey(CODE_SPREAD)!);
    }

    if (this.getMetricByKey(CODE_COLOR)) {
      this.colorMetric = new ColorMetric(this.getMetricByKey(CODE_COLOR)!);
    }

    if (this.getMetricByKey(CODE_DEAD_ZONES)) {
      this.deadZoneMetric = new DeadZoneMetric(this.getMetricByKey(CODE_DEAD_ZONES)!);
    }

    if (this.getMetricByKey(CODE_PAVILLION_COLOR_VIBOX)) {
      this.pavillionColorViboxMetric = new PavillionColorViboxMetric(this.getMetricByKey(CODE_PAVILLION_COLOR_VIBOX)!);
    }

    if (this._embedded?.scores?.hue && MunsellMetric.isValidMetric(this._embedded.scores.hue)) {
      this.fancyColorGrade = new MunsellMetric(this._embedded.scores.hue);
    }

    this.color = Number.isInteger(this.color)
      ? dictionaryRepository.getColorById(this.color as unknown as number)
      : this.color;
    this.clarity = Number.isInteger(this.clarity)
      ? dictionaryRepository.getClarityById(this.clarity as unknown as number)
      : this.clarity;
    this.cutQuality = Number.isInteger(this.cutQuality)
      ? dictionaryRepository.getCutQualityById(this.cutQuality as unknown as number)
      : this.cutQuality;
    this.fancyGrade = Number.isInteger(this.fancyGrade)
      ? dictionaryRepository.getFancyGradeById(this.fancyGrade as unknown as number)
      : this.fancyGrade;
    this.fancyColorDescHue = Number.isInteger(this.fancyColorDescHue)
      ? dictionaryRepository.getFancyColorDescHueById(this.fancyColorDescHue as unknown as number)
      : this.fancyColorDescHue;
    this.fancyColorDescModifier = Number.isInteger(this.fancyColorDescModifier)
      ? dictionaryRepository.getColorModifierById(this.fancyColorDescModifier as unknown as number)
      : this.fancyColorDescModifier;
    this.girdleThicknessGrade = Number.isInteger(this.girdleThicknessGrade)
      ? dictionaryRepository.getGirdleThicknessGradeById(this.girdleThicknessGrade as unknown as number)
      : this.girdleThicknessGrade;
    this.girdleThicknessMax = Number.isInteger(this.girdleThicknessMax)
      ? dictionaryRepository.getGirdleThicknessGradeById(this.girdleThicknessMax as unknown as number)
      : this.girdleThicknessMax;
    this.fluorescenceColor = Number.isInteger(this.fluorescenceColor)
      ? dictionaryRepository.getFluorescenceColorById(this.fluorescenceColor as unknown as number)
      : this.fluorescenceColor;
    this.symmetry = Number.isInteger(this.symmetry)
      ? dictionaryRepository.getSymmetryById(this.symmetry as unknown as number)
      : this.symmetry;
    this.polish = Number.isInteger(this.polish)
      ? dictionaryRepository.getPolishById(this.polish as unknown as number)
      : this.polish;
    this.culetSizeGrade = Number.isInteger(this.culetSizeGrade)
      ? dictionaryRepository.getCuletSizeGradeById(this.culetSizeGrade as unknown as number)
      : this.culetSizeGrade;
    this.fluorescenceStrength = Number.isInteger(this.fluorescenceStrength)
      ? dictionaryRepository.getFluorescenceStrengthById(this.fluorescenceStrength as unknown as number)
      : this.fluorescenceStrength;

    if (this.certifications) {
      this.certifications.forEach((c) => {
        c.laboratory = Number.isInteger(c.laboratory)
          ? dictionaryRepository.getLaboratoryById(c.laboratory as unknown as number)
          : c.laboratory;
      });
    }

    if (this.details) {
      if (Array.isArray(this.details.blackInc)) {
        this.blackInc = this.details.blackInc;
      }

      if (Array.isArray(this.details.whiteInc)) {
        this.whiteInc = this.details.whiteInc;
      }

      if (Array.isArray(this.details.openInc)) {
        this.openInc = this.details.openInc;
      }

      if (Array.isArray(this.details.treatment)) {
        this.treatment = this.details.treatment;
      }
    }
  }

  override toGlobalAccessTarget(): GlobalAccessTarget {
    return new GlobalAccessTarget({
      id: this.id,
      access: this.access,
      dataAccess: this.dataAccess,
      hasDataAccess: true,
    });
  }

  private applyDemoFCRFGrade() {
    const demo = Storage.getObject(KEY_FCRF_DEMO, Storage.STORAGE_SESSION);
    const grade = demo?.[this.id];

    if (!grade) {
      return;
    }

    this.fcrfGrade = new FCRFGrade(JSON.parse(grade) as FCRFGradeModel);
  }
}
